import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { AppearanceCard } from "~/components/appearance-card";
import { Field, fieldMessageId } from "~/components/field";
import { NotifyPrompt } from "~/components/notify-prompt";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { PasswordInput } from "~/components/ui/password-input";
import { api } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { formatE164 } from "~/lib/phone";

export const Route = createFileRoute("/_authed/_portal/account")({
  component: Account,
});

const SAVE_FAILED = "We could not save that. Check your connection and try again.";

function useSavedFlash(): [boolean, () => void] {
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);
  return [saved, () => setSaved(true)];
}

function Account() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function signOut() {
    try {
      await api.logout();
    } finally {
      qc.clear();
      await navigate({ to: "/login" });
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="type-title">Account</h1>
        <p className="text-muted-foreground type-body tabular mt-1">{me?.phone ? formatE164(me.phone) : ""}</p>
      </header>

      <DetailsSection />
      <PasswordSection />

      <section className="border-hairline bg-card rounded-xl border p-5">
        <h2 className="type-headline">Notifications</h2>
        <p className="text-muted-foreground type-footnote mt-1 mb-4">
          We always text you. Notifications on this device are faster and work when the app is closed.
        </p>
        <NotifyPrompt showWhenOn title="Get notified on this device" body="Parcel updates and replies from our team." />
      </section>

      <AppearanceCard />

      <Button variant="outline" size="touch" className="w-full" onClick={() => void signOut()}>
        Sign out
      </Button>
    </div>
  );
}

function DetailsSection() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [town, setTown] = useState("");
  const [county, setCounty] = useState("");
  const [errors, setErrors] = useState<{ name?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [saved, flashSaved] = useSavedFlash();

  useEffect(() => {
    if (!me) return;
    setName(me.name ?? "");
    setArea(me.locationArea ?? "");
    setTown(me.town ?? "");
    setCounty(me.county ?? "");
  }, [me]);

  async function save(e: FormEvent) {
    e.preventDefault();
    const fullName = name.trim().replace(/\s+/g, " ");
    if (fullName.length < 2) return setErrors({ name: "Enter your name so we know who your parcels belong to." });
    setBusy(true);
    setErrors({});
    try {
      const updated = await api.updateProfile({ name: fullName, locationArea: area.trim(), town: town.trim(), county: county.trim() });
      qc.setQueryData(["me"], updated);
      flashSaved();
    } catch {
      setErrors({ form: SAVE_FAILED });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="border-hairline bg-card space-y-5 rounded-xl border p-5" onSubmit={save} noValidate>
      <h2 className="type-headline">Your details</h2>
      <Field id="name" label="Full name" error={errors.name}>
        <Input
          id="name"
          density="comfortable"
          autoComplete="name"
          value={name}
          aria-invalid={!!errors.name || undefined}
          aria-describedby={errors.name ? fieldMessageId("name") : undefined}
          onChange={(e) => {
            setName(e.target.value);
            setErrors({});
          }}
        />
      </Field>

      <fieldset className="space-y-3">
        <legend className="text-[14px] font-medium">Home address</legend>
        <p className="text-muted-foreground type-footnote">Only used when you choose home delivery.</p>
        <Input
          density="comfortable"
          aria-label="Area or estate"
          placeholder="Area or estate, for example Westlands"
          autoComplete="address-level3"
          value={area}
          onChange={(e) => setArea(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input density="comfortable" aria-label="Town" placeholder="Town" autoComplete="address-level2" value={town} onChange={(e) => setTown(e.target.value)} />
          <Input density="comfortable" aria-label="County" placeholder="County" autoComplete="address-level1" value={county} onChange={(e) => setCounty(e.target.value)} />
        </div>
      </fieldset>

      {errors.form && (
        <p role="alert" className="text-destructive type-footnote">
          {errors.form}
        </p>
      )}
      <Button type="submit" variant="secondary" size="touch" className="w-full" loading={busy}>
        {saved && <Check />}
        {saved ? "Saved" : "Save changes"}
      </Button>
    </form>
  );
}

function PasswordSection() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, flashSaved] = useSavedFlash();

  async function save(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) return setError("Use at least 8 characters.");
    setBusy(true);
    setError(null);
    try {
      await api.setPassword(password);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setPassword("");
      setEditing(false);
      flashSaved();
    } catch {
      setError(SAVE_FAILED);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="border-hairline bg-card rounded-xl border p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="type-headline">Password</h2>
          <p className="text-muted-foreground type-footnote mt-1">
            {saved
              ? "Password saved."
              : me?.hasPassword
                ? "You can sign in with your password or a code."
                : "Optional. Sign in without waiting for a code."}
          </p>
        </div>
        {!editing && (
          <Button variant="outline" className="h-11 shrink-0 px-4 text-[14px]" onClick={() => setEditing(true)}>
            {me?.hasPassword ? "Change" : "Set one"}
          </Button>
        )}
      </div>

      {editing && (
        <form className="mt-4 space-y-4" onSubmit={save} noValidate>
          <Field id="new-password" label="New password" error={error} hint="At least 8 characters.">
            <PasswordInput
              id="new-password"
              autoFocus
              autoComplete="new-password"
              className="h-12 rounded-lg pl-3.5 text-[16px]"
              value={password}
              aria-invalid={!!error || undefined}
              aria-describedby={fieldMessageId("new-password")}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
            />
          </Field>
          <div className="flex gap-3">
            <Button type="button" variant="outline" size="touch" className="flex-1" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" size="touch" className="flex-1" loading={busy}>
              Save
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
